import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Home as HomeIcon,
  Architecture as ArchitectureIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Map as MapIcon,
  Calculate as CalculateIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CloudDone as CloudIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Analytics as AnalyticsIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  details: string[];
}

const features: Feature[] = [
  {
    icon: <MapIcon fontSize="large" />,
    title: 'Real-Time GIS Data Integration',
    description: 'Access live property data from authenticated Massachusetts GIS sources',
    details: [
      'Authenticated ArcGIS Enterprise APIs',
      'MassGIS Level 3 parcel data',
      'Real-time zoning district verification',
      'Accurate lot dimensions from geometry',
      'Building footprint extraction',
      'Chamfered corner detection and correction',
    ],
  },
  {
    icon: <ArchitectureIcon fontSize="large" />,
    title: 'Development Scenario Analysis',
    description: 'Explore multiple development options with detailed projections',
    details: [
      'Renovation and gut renovation options',
      'Horizontal and vertical extensions',
      'Complete teardown and rebuild analysis',
      'ADU (Accessory Dwelling Unit) feasibility',
      'Mixed-use development potential',
      'Subdivision possibility assessment',
    ],
  },
  {
    icon: <CalculateIcon fontSize="large" />,
    title: 'Advanced Financial Modeling',
    description: 'Comprehensive financial analysis with ROI calculations',
    details: [
      'Net Present Value (NPV) calculations',
      'Internal Rate of Return (IRR) analysis',
      '10-year cash flow projections',
      'Construction cost estimation',
      'Market rent and sale comparisons',
      'Tax impact analysis',
      'Financing scenario modeling',
    ],
  },
  {
    icon: <GavelIcon fontSize="large" />,
    title: 'Zoning Compliance Engine',
    description: 'Automated verification against municipal zoning ordinances',
    details: [
      'Somerville district-specific analysis (NR, UR, MR, CC, etc.)',
      'Cambridge traditional zoning (A, B, C districts)',
      'Building type optimization',
      'Setback calculations',
      'Height and density restrictions',
      'Special permit identification',
      'Inclusionary housing requirements',
    ],
  },
  {
    icon: <BuildIcon fontSize="large" />,
    title: 'Professional Floor Plans',
    description: 'AI-generated floor plans optimized for Massachusetts building codes',
    details: [
      '780 CMR compliant layouts',
      'Room-by-room specifications',
      'ADA accessibility features',
      'Natural light optimization',
      'HVAC and electrical planning',
      'Multiple floor configurations',
      'Export to CAD formats',
    ],
  },
  {
    icon: <AnalyticsIcon fontSize="large" />,
    title: 'Market Intelligence',
    description: 'Real-time market data and trend analysis',
    details: [
      'Comparable property analysis',
      'Rental rate trends by neighborhood',
      'Sale price comparisons',
      'Days on market statistics',
      'Absorption rate calculations',
      'Investment potential scoring',
      'Neighborhood growth indicators',
    ],
  },
  {
    icon: <TrendingUpIcon fontSize="large" />,
    title: 'AI-Powered Recommendations',
    description: 'Machine learning insights for optimal development strategies',
    details: [
      'Property scoring algorithm (0-100 scale)',
      'Development strategy recommendations',
      'Market timing suggestions',
      'Risk assessment and mitigation',
      'Green building opportunities',
      'Unit mix optimization',
      'Predicted ROI confidence levels',
    ],
  },
  {
    icon: <AssessmentIcon fontSize="large" />,
    title: 'Comprehensive Reporting',
    description: 'Professional reports ready for investors and lenders',
    details: [
      'Executive summary generation',
      'Detailed feasibility reports',
      'Visual data presentations',
      'Export to PDF and Excel',
      '3D visualizations',
      'Permit requirement checklists',
      'Timeline and milestone planning',
    ],
  },
  {
    icon: <SecurityIcon fontSize="large" />,
    title: 'Enterprise Security',
    description: 'Bank-level security for your sensitive property data',
    details: [
      'End-to-end encryption',
      'SOC 2 Type II compliance',
      'Role-based access control',
      'Audit trail logging',
      'Data residency controls',
      'Regular security audits',
      'GDPR compliant',
    ],
  },
];

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" color="primary" sx={{ mr: 2 }} onClick={() => navigate('/')}>
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 600 }}>
            QuickBuildr.ai
          </Typography>
          {isAuthenticated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              fontWeight={700}
              color="primary.main"
              gutterBottom
            >
              Powerful Features for Smart Development
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
            >
              Everything you need to analyze, plan, and execute successful real estate projects in Massachusetts
            </Typography>
          </motion.div>

          {/* Feature Grid */}
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ color: 'primary.main', mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {feature.description}
                      </Typography>
                      
                      <Accordion elevation={0} sx={{ bgcolor: 'transparent' }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ px: 0 }}
                        >
                          <Typography variant="body2" color="primary">
                            View Details
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0 }}>
                          {feature.details.map((detail, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                              <CheckIcon 
                                fontSize="small" 
                                color="success" 
                                sx={{ mr: 1, mt: 0.3, fontSize: '16px' }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {detail}
                              </Typography>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Integration Section */}
          <Box sx={{ mt: 10, p: 6, bgcolor: 'white', borderRadius: 2 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Seamless Integrations
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              QuickBuildr.ai integrates with the tools and services you already use
            </Typography>
            
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <CloudIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Cloud Storage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Export reports directly to Google Drive, Dropbox, or OneDrive
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <SpeedIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    API Access
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Integrate our analysis engine into your existing workflows
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <SecurityIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    SSO Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enterprise single sign-on with SAML 2.0 support
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* CTA Section */}
          <Box sx={{ mt: 10, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Ready to experience these features?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Start analyzing properties with the most comprehensive tool in Massachusetts
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/register')}
                sx={{ px: 4 }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => navigate('/pricing')}
                sx={{ px: 4 }}
              >
                View Pricing
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8 }}>
            © 2025 QuickBuildr.ai. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturesPage;